import React, { useState } from 'react';
import Formatters from 'formatters/_Formatter';
import RecordDetailsLink from 'formatters/RecordDetailsLink';
import { BIS, COMBINED } from 'data/dataSourceConstants';
import Highlight from 'components/Highlight';
import SortableHeader from 'components/dataTable/SortableHeader';
import FileUploadButton from 'components/dataTable/FileUploadButton';
import JobApplicationCloseWarning from 'formatters/JobApplicationCloseWarning';
import JobApplicationWithdrawnNote from 'formatters/JobApplicationWithdrawnNote';

import './JobApplicationsGroupedTable.css';

function renderCell(col, row, context, options = {}) {
	const { propertyData, dataSet, dataSource, searchString } = context;
	const { showCloseWarning = false } = options;
	let rawContent = row[col.key] || row[col.altKey];

	if (col.key === 'doc_number' || col.key === 'doc__') {
		rawContent = row.docLabel;
	}

	if (col.key === 'job_status' || col.key === 'job_status_descrp') {
		rawContent = row.displayStatus;
	}

	let cellContent = rawContent || '-';

	if (col.formatter) {
		const Formatter = Formatters[col.formatter];
		if (Formatter) {
			if (col.formatter === 'ResolutionRequest') {
				cellContent = (
					<Formatter
						text={rawContent}
						rowData={row}
						propertyData={propertyData}
						dataSet={dataSet}
						dataSource={dataSource}
					/>
				);
			} else if (col.formatter === 'StatusBadge' && rawContent) {
				cellContent = (
					<span className="job-application-status-cell">
						<Formatter
							text={rawContent}
							rowData={{ ...row, isOpen: row.applicationIsOpen }}
							propertyData={propertyData}
							dataSet={dataSet}
							dataSource={dataSource}
						/>
						{showCloseWarning && <JobApplicationCloseWarning />}
						{/* BIS-only: set when the detail page carried a withdrawal
						    banner, which is what makes this row read as resolved
						    even though its status text still looks open. */}
						<JobApplicationWithdrawnNote withdrawnDate={row.withdrawn_date} />
					</span>
				);
			} else if (rawContent) {
				cellContent = (
					<Formatter
						text={rawContent}
						rowData={row}
						propertyData={propertyData}
						dataSet={dataSet}
						dataSource={dataSource}
					/>
				);
			}
		}
	}

	return (
		<td>
			{typeof cellContent === 'string' ? (
				<Highlight text={cellContent} substring={searchString} />
			) : (
				cellContent
			)}
		</td>
	);
}

function JobApplicationsGroupedTable(props) {
	const {
		columns,
		groups,
		dataSet,
		dataSource,
		propertyData,
		isLoading,
		searchString,
		sortField,
		sortDirection,
		onSort,
	} = props;

	const [expandedGroups, setExpandedGroups] = useState({});

	const toggleGroup = (jobNumber) => {
		setExpandedGroups((prev) => ({
			...prev,
			[jobNumber]: !prev[jobNumber],
		}));
	};

	const isGroupExpanded = (jobNumber) => expandedGroups[jobNumber] === true;

	let noDataRow = (
		<tr>
			<td colSpan={columns.length + 1} className="nothing-found-row">
				No Data Available
			</td>
		</tr>
	);

	if (isLoading) {
		noDataRow = (
			<tr>
				<td colSpan={columns.length + 1} className="nothing-found-row">
					Loading...
				</td>
			</tr>
		);
	}

	const cellContext = { propertyData, dataSet, dataSource, searchString };
	const isDrawingsCol = (col) => col.formatter === 'ResolutionRequest';
	const isJobNumberCol = (col) =>
		(col.key === 'job_number' || col.key === 'job__') && !isDrawingsCol(col);
	const isDocNumberCol = (col) =>
		col.key === 'doc_number' || col.key === 'doc__';

	return (
		<table className="table table-striped data-table job-applications-grouped-table">
			<thead>
				<tr>
					<th></th>
					{columns.map((col, i) => (
						<SortableHeader
							key={`${col.key}-${col.label}-${i}`}
							column={col}
							sortField={sortField}
							sortDirection={sortDirection}
							onSort={onSort}
						/>
					))}
				</tr>
			</thead>
			<tbody>
				{(groups.length === 0 || isLoading) && noDataRow}
				{!isLoading &&
					groups.map((group) => {
						const expanded = isGroupExpanded(group.jobNumber);
						const hasMultipleDocs = group.documents.length > 1;

						if (!hasMultipleDocs) {
							const doc = group.documents[0];
							const docRow = {
								...doc,
								applicationIsOpen:
									group.applicationStatus === 'open' ? 1 : 0,
							};
							return (
								<tr
									key={group.jobNumber}
									className="job-application-doc-row"
								>
									<td className="data-table-buttons-cell">
										<div className="data-table-buttons-wrapper">
											<RecordDetailsLink
												isBis={dataSource === BIS}
												dataSource={dataSource}
												row={doc}
												dataSet={dataSet}
											/>
											{dataSource !== COMBINED && (
												<FileUploadButton
													dataSet={dataSet}
													dataSource={dataSource}
													row={doc}
												/>
											)}
										</div>
									</td>
									{columns.map((col, j) => {
										if (isDrawingsCol(col)) {
											return (
												<React.Fragment
													key={`${doc.id || group.jobNumber}-drawings-${j}`}
												>
													{renderCell(col, doc, cellContext)}
												</React.Fragment>
											);
										}
										if (isJobNumberCol(col)) {
											return (
												<td key={`${group.jobNumber}-single-${col.key}`}>
													<Highlight
														text={String(group.jobNumber)}
														substring={searchString}
													/>
												</td>
											);
										}
										const isStatusCol =
											col.key === 'job_status' ||
											col.key === 'job_status_descrp';
										return (
											<React.Fragment
												key={`${doc.id || group.jobNumber}-${col.key}-${j}`}
											>
												{renderCell(
													col,
													docRow,
													cellContext,
													{
														showCloseWarning:
															isStatusCol && group.needsCloseWarning,
													}
												)}
											</React.Fragment>
										);
									})}
								</tr>
							);
						}

						const headerDoc = group.documents[0];
						const subDocuments = group.documents.slice(1);
						const headerDocRow = {
							...headerDoc,
							applicationIsOpen:
								group.applicationStatus === 'open' ? 1 : 0,
						};

						return (
							<React.Fragment key={group.jobNumber}>
								<tr className="job-application-group-header">
									<td className="data-table-buttons-cell">
										<div className="data-table-buttons-wrapper">
											<RecordDetailsLink
												isBis={dataSource === BIS}
												dataSource={dataSource}
												row={headerDoc}
												dataSet={dataSet}
											/>
											{dataSource !== COMBINED && (
												<FileUploadButton
													dataSet={dataSet}
													dataSource={dataSource}
													row={headerDoc}
												/>
											)}
										</div>
									</td>
									{columns.map((col) => {
										if (isDrawingsCol(col)) {
											return (
												<React.Fragment
													key={`${group.jobNumber}-header-drawings`}
												>
													{renderCell(col, headerDoc, cellContext)}
												</React.Fragment>
											);
										}

										if (isJobNumberCol(col)) {
											return (
												<td key={`${group.jobNumber}-header-${col.key}`}>
													<Highlight
														text={String(group.jobNumber)}
														substring={searchString}
													/>
												</td>
											);
										}

										if (isDocNumberCol(col)) {
											return (
												<td key={`${group.jobNumber}-header-${col.key}`}>
													<span className="job-doc-cell-content">
														<Highlight
															text={headerDoc.docLabel || '-'}
															substring={searchString}
														/>
														<button
															type="button"
															className="btn btn-hyperlink job-group-toggle"
															onClick={() => toggleGroup(group.jobNumber)}
															aria-label={
																expanded ? 'Collapse group' : 'Expand group'
															}
														>
															<i
																className={`bi bi-chevron-${expanded ? 'down' : 'right'}`}
															/>
														</button>
													</span>
												</td>
											);
										}

										const isStatusCol =
											col.key === 'job_status' ||
											col.key === 'job_status_descrp';
										return (
											<React.Fragment
												key={`${group.jobNumber}-header-${col.key}`}
											>
												{renderCell(col, headerDocRow, cellContext, {
													showCloseWarning:
														isStatusCol &&
														group.needsCloseWarning &&
														!expanded,
												})}
											</React.Fragment>
										);
									})}
								</tr>
								{expanded &&
									subDocuments.map((doc, docIndex) => {
										const isLastDoc =
											docIndex === subDocuments.length - 1;
										const showSubRowWarning =
											group.needsCloseWarning && isLastDoc;

										return (
										<tr
											key={doc.id || `${group.jobNumber}-doc-${docIndex}`}
											className="job-application-doc-row job-application-sub-row"
										>
											<td className="data-table-buttons-cell">
												<div className="data-table-buttons-wrapper">
													<RecordDetailsLink
														isBis={dataSource === BIS}
														dataSource={dataSource}
														row={doc}
														dataSet={dataSet}
													/>
													{dataSource !== COMBINED && (
														<FileUploadButton
															dataSet={dataSet}
															dataSource={dataSource}
															row={doc}
														/>
													)}
												</div>
											</td>
											{columns.map((col, j) => {
												if (isDrawingsCol(col)) {
													return (
														<React.Fragment
															key={`${doc.id || docIndex}-drawings-${j}`}
														>
															{renderCell(col, doc, cellContext)}
														</React.Fragment>
													);
												}
												if (isJobNumberCol(col)) {
													return (
														<td key={`${doc.id || docIndex}-job-${j}`}>
															<Highlight
																text={String(group.jobNumber)}
																substring={searchString}
															/>
														</td>
													);
												}
												const isStatusCol =
													col.key === 'job_status' ||
													col.key === 'job_status_descrp';
												return (
													<React.Fragment
														key={`${doc.id || docIndex}-${col.key}-${j}`}
													>
														{renderCell(
															col,
															{
																...doc,
																applicationIsOpen:
																	group.applicationStatus === 'open'
																		? 1
																		: 0,
															},
															cellContext,
															{
																showCloseWarning:
																	isStatusCol && showSubRowWarning,
															}
														)}
													</React.Fragment>
												);
											})}
										</tr>
										);
									})}
							</React.Fragment>
						);
					})}
			</tbody>
		</table>
	);
}

export default JobApplicationsGroupedTable;
